import Button from '@jetbrains/ring-ui/components/button/button'
import classNames from 'classnames'
import * as React from 'react'
import type {CSSProperties} from 'react'

import {useOneLine} from '../../../contexts/oneLine'
import type {Branch} from '../../../services/rest'
import {stringifyId} from '../../../types'
import type {BuildId, BuildTypeId} from '../../../types'
import {emptyArray, noop} from '../../../utils/empty'
import Grid from '../Grid/Grid'
import RowGroup from '../Grid/RowGroup'
import RowGroupsWrapper from '../Grid/RowGroupsWrapper'

import BuildPlaceholder from './Build/Build.placeholder'
import BuildDetails from './BuildDetails/BuildDetails'
import {useBuildsClassName} from './Builds'
import QueuedToggler from './QueuedToggler/QueuedToggler'

import buildsStyles from './Builds.css'
import styles from './Builds.wrapped.css'

type CommonProps = {
  queuedVisible?: boolean
  className?: string
  onToggle?: () => unknown
  buildTypeId?: BuildTypeId | null | undefined
  hasParentGrid?: boolean
  inHierarchy?: boolean
}
type WrapperProps = CommonProps & {
  totalQueuedCount?: number
  children: React.ReactNode
  queuedPresent: boolean
}

export function BuildsWrapper({
  totalQueuedCount = 0,
  onToggle = noop,
  queuedVisible,
  children,
  className,
  queuedPresent,
  hasParentGrid,
  buildTypeId,
  inHierarchy,
}: WrapperProps) {
  const buildsClasses = classNames(styles.buildListWrapper, className, {
    [styles.withQueued]: queuedPresent,
    [styles.hasParentGrid]: hasParentGrid,
    [useBuildsClassName()]: !hasParentGrid,
  })
  return (
    <div
      className={classNames(styles.content, {
        [styles.hasParentGrid]: hasParentGrid,
        [styles.inHierarchy]: inHierarchy,
      })}
    >
      {queuedPresent && (
        <QueuedToggler
          count={totalQueuedCount}
          show={queuedVisible === true}
          onToggle={onToggle}
          buildTypeId={buildTypeId}
          containerClassName={classNames(styles.queuedWrapper, styles.queuedToggler)}
          buttonClassName={styles.queuedButton}
        />
      )}
      {hasParentGrid === true ? (
        <div className={buildsClasses}>{children}</div>
      ) : (
        <Grid className={buildsClasses}>{children}</Grid>
      )}
    </div>
  )
}

type ShowMoreQueuedBuildsProps = {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}
function ShowMoreQueuedBuilds({onClick}: ShowMoreQueuedBuildsProps) {
  return (
    <div className={styles.queuedWrapper}>
      <Button className={styles.queuedButton} onClick={onClick} text>
        {'Show more'}
      </Button>
    </div>
  )
}

const QUEUED_BUILDS_COUNT_STEP = 15
type Props = CommonProps & {
  wrapperClassName?: string
  buildIds: ReadonlyArray<BuildId>
  queuedBuildIds?: ReadonlyArray<BuildId>
  queuedBuildsCountToShow?: number
  queuedBuildsCountStep?: number
  queuedTogglerHidden?: boolean
  setShowQueuedBuildsCount?: (count: number, buildTypeId: BuildTypeId, branch?: Branch) => unknown
  style?: CSSProperties
  nested?: boolean
}

function BuildsWrapped({
  queuedBuildIds = emptyArray,
  buildIds = emptyArray,
  queuedVisible = false,
  queuedBuildsCountStep = QUEUED_BUILDS_COUNT_STEP,
  queuedBuildsCountToShow = queuedBuildsCountStep,
  queuedTogglerHidden = false,
  setShowQueuedBuildsCount,
  buildTypeId,
  wrapperClassName,
  style,
  nested,
  ...props
}: Props) {
  const oneLine = useOneLine()

  if (queuedBuildIds.length === 0 && buildIds.length === 0) {
    return (
      <RowGroup>
        <div className={styles.noBuilds}>{'No builds yet'}</div>
      </RowGroup>
    )
  }

  const totalQueuedCount = queuedBuildIds.length
  const queuedPresent = totalQueuedCount > 0 && !queuedTogglerHidden
  return (
    <RowGroupsWrapper className={wrapperClassName} style={style}>
      <BuildsWrapper
        queuedVisible={queuedVisible}
        totalQueuedCount={totalQueuedCount}
        queuedPresent={queuedPresent}
        buildTypeId={buildTypeId}
        {...props}
      >
        {queuedVisible &&
          queuedBuildIds
            .slice(0, queuedBuildsCountToShow)
            .map(buildId => (
              <BuildDetails
                inHierarchy={props.inHierarchy}
                wrapped
                key={stringifyId(buildId)}
                buildId={buildId}
                alwaysShowLineBorders
                nested={nested}
              />
            ))}

        {setShowQueuedBuildsCount != null &&
          buildTypeId != null &&
          queuedPresent &&
          queuedVisible &&
          queuedBuildsCountToShow < queuedBuildIds.length && (
            <RowGroup>
              <div className={styles.showMoreQueuedBulds}>
                <ShowMoreQueuedBuilds
                  onClick={() =>
                    setShowQueuedBuildsCount(
                      queuedBuildsCountToShow + queuedBuildsCountStep,
                      buildTypeId,
                    )
                  }
                />
              </div>
            </RowGroup>
          )}

        {oneLine === true && (
          <RowGroup>
            <div className={buildsStyles.oneLineMargin} />
          </RowGroup>
        )}
        {buildIds.map(buildId => (
          <BuildDetails
            wrapped
            key={stringifyId(buildId)}
            buildId={buildId}
            inHierarchy={props.inHierarchy}
            nested={nested}
          />
        ))}
        {oneLine === true && (
          <RowGroup>
            <div className={buildsStyles.oneLineMargin} />
          </RowGroup>
        )}
      </BuildsWrapper>
    </RowGroupsWrapper>
  )
}

export default React.memo(BuildsWrapped)
type BuildsWrappedPlaceholderProps = {
  queuedVisible?: boolean
  onlyQueuedPresented?: boolean
  queuedCountToRender?: number
  hasParentGrid?: boolean
  className?: string
}

function getPlaceholderCount({
  queuedVisible = false,
  onlyQueuedPresented = false,
  queuedCountToRender = 0,
}: BuildsWrappedPlaceholderProps): number {
  let count = 1

  if (queuedVisible) {
    count = queuedCountToRender + 1
  }

  if (onlyQueuedPresented) {
    count = queuedCountToRender
  }

  return count
}

export const BuildsWrappedPlaceholder: React.ComponentType<BuildsWrappedPlaceholderProps> =
  React.memo(function BuildsWrappedPlaceholder(props: BuildsWrappedPlaceholderProps) {
    const {queuedCountToRender = 0, hasParentGrid = false, className} = props
    const oneLine = useOneLine()
    const count = getPlaceholderCount(props)
    const placeholder = React.useMemo(() => {
      const result = [
        oneLine === true && (
          <RowGroup key="marginTop">
            <div className={buildsStyles.oneLineMargin} />
          </RowGroup>
        ),
      ]

      for (let i = 0; i < count; i++) {
        result.push(<BuildPlaceholder key={i} wrapped />)
      }

      result.push(
        oneLine === true && (
          <RowGroup key="marginBottom">
            <div className={buildsStyles.oneLineMargin} />
          </RowGroup>
        ),
      )
      return result
    }, [count, oneLine])
    const buildsClasses = classNames(styles.buildListWrapper, {
      [styles.withQueued]: queuedCountToRender > 0,
      [styles.hasParentGrid]: hasParentGrid,
      [useBuildsClassName()]: !hasParentGrid,
    })
    return (
      <RowGroupsWrapper className={className}>
        <div
          className={classNames(styles.content, {
            [styles.hasParentGrid]: hasParentGrid,
          })}
        >
          {hasParentGrid ? (
            <div className={buildsClasses}>{placeholder}</div>
          ) : (
            <Grid className={buildsClasses}>{placeholder}</Grid>
          )}
        </div>{' '}
      </RowGroupsWrapper>
    )
  })
